import type { Education } from '@seek/ca-myprofile-utils';

import type { GraphqlLicence } from 'src/components/Profile/Licences/types';
import { verifiableCredentialTypenameToCertsyType } from 'src/components/Profile/Verifications/analyticsUtils';
import type {
  Verifiable,
  Verified,
} from 'src/components/Profile/Verifications/types';
import type { VerificationStatus } from 'src/modules/analytics/types';
import { CredentialExpiryStatus } from 'src/types/graphql';

export const calculateLicenceAnalytics = (licences: GraphqlLicence[]) => {
  // Verified licences have credentials
  // Not verified licences do not have credentials but have a verification URL
  // Unverifiable licences do not have credentials or a verification URL
  // Verified expired licences have an `Expired` status in the credential

  // Not verified licences and unverifiable licences are mutually exclusive

  const licencesNoCredentials = licences.filter(
    (licence) => !Boolean(licence.credential),
  );
  const licencesNoVerificationUrl = licencesNoCredentials.filter(
    (licence) => !Boolean(licence.verificationUrl),
  );
  const verifiedExpiredLicences = licences.filter(
    (licence) =>
      licence?.credential?.expiryStatus === CredentialExpiryStatus.Expired,
  );

  return {
    licencesVerifiedTotal: licences.length - licencesNoCredentials.length,
    licencesNotVerifiedTotal:
      licencesNoCredentials.length - licencesNoVerificationUrl.length,
    licencesUnverifiableTotal: licencesNoVerificationUrl.length,
    licencesExpiredVerifiedTotal: verifiedExpiredLicences.length,
  };
};

export const calculateQualificationAnalytics = (
  qualifications: Education[],
) => {
  const qualificationsNoCredentials = qualifications.filter(
    (qualification) => !Boolean(qualification.credential),
  );
  const qualificationsNoVerificationUrl = qualificationsNoCredentials.filter(
    (qualification) => !Boolean(qualification.verificationUrl),
  );

  return {
    qualificationsVerifiedTotal:
      qualifications.length - qualificationsNoCredentials.length,
    qualificationsNotVerifiedTotal:
      qualificationsNoCredentials.length -
      qualificationsNoVerificationUrl.length,
    qualificationsUnverifiableTotal: qualificationsNoVerificationUrl.length,
  };
};

export const getVerificationStatus = (
  verification: GraphqlLicence | Education,
): VerificationStatus => {
  if (verification?.credential) {
    return 'verified';
  }

  if (verification?.verificationUrl) {
    return 'not-verified';
  }

  return 'unverifiable';
};

export const calculateVerificationsAnalytics = (
  verified: Verified | null | undefined,
  verifiable: Verifiable | null | undefined,
) => {
  let credentialsVerifiedTotal;
  let credentialsVerifiedType;
  let credentialsNotVerifiedTotal;
  let credentialsNotVerifiedType;

  if (verified) {
    const verifiedCredentials = verified.flatMap(
      (verification) => verification.credentials,
    );

    credentialsVerifiedTotal = verifiedCredentials.length;
    const verifiedCredentialUniqueTypes = [
      ...new Set(
        verifiedCredentials.map((credential) => credential?.credentialType),
      ),
    ];
    credentialsVerifiedType = verifiedCredentialUniqueTypes.sort().join(',');
  }

  if (verifiable) {
    const verifiableCredentials = verifiable.flatMap(
      (verification) => verification.credentials,
    );
    credentialsNotVerifiedTotal = verifiableCredentials.length;
    const verifiableCredentialUniqueTypes = [
      ...new Set(
        verifiableCredentials
          .map((credential) =>
            credential && '__typename' in credential
              ? verifiableCredentialTypenameToCertsyType[credential?.__typename]
              : null,
          )
          .filter((type) => type !== null),
      ),
    ];
    credentialsNotVerifiedType = verifiableCredentialUniqueTypes
      .sort()
      .join(',');
  }

  return {
    credentialsVerifiedTotal,
    credentialsVerifiedType,
    credentialsNotVerifiedTotal,
    credentialsNotVerifiedType,
  };
};
